import * as React from "react";

import SubmitButton from "./submitButton";
import { useIsMonthEndLockActive } from "hooks/useMonthEndStatus";

export type MaintenanceLockSubmitButtonProps = React.ComponentProps<typeof SubmitButton>;

/**
 * A SubmitButton that disables itself while the Vital Objects month-end job is running.
 *
 * Use it on any form that renders MonthEndMaintenanceAlert. It reads the same state the alert does,
 * so the two can never disagree: whenever the alert is explaining that updates are unavailable this
 * button is disabled, and both clear together on the next refresh. An enabled submit button sitting
 * under that alert invites the member to try anyway, and teaches them to distrust the alert when it
 * turns out to be right.
 *
 * This is a signal, not the protection. The save thunks still call
 * isSaveBlockedByMonthEndMaintenance, which is what covers the job starting while the form is open
 * and the status on screen being stale. Do not remove that check on the strength of this button.
 *
 * Reads the lock itself, like MaintenanceLockButton, so the class-component forms do not have to
 * thread a prop down.
 */
export function MaintenanceLockSubmitButton(props: MaintenanceLockSubmitButtonProps) {
    const isMonthEndLockActive = useIsMonthEndLockActive();

    // Cast as SubmitButton itself does when forwarding to NoEmulationButton: the inherited MUI
    // ButtonProps types ref for an HTMLButtonElement, which does not line up with a class component.
    return <SubmitButton {...(props as any)} disabled={props.disabled || isMonthEndLockActive} />;
}

export default MaintenanceLockSubmitButton;
